import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useEffect, useEffectEvent } from "react";
import { keepPreviousData, useQuery } from "react-query";

import { useTipTapEditorContext } from "../TipTapEditorContext";

interface LinkPreview {
  description: string;
  title: string;
  url: string;
}

export const useUnfurlLink = ({
  linkHrefs,
  setHasLinks,
}: {
  linkHrefs: string[];
  setHasLinks: (hasLinks: boolean) => void;
}) => {
  const { linkPreviews, setLinkPreviews, editor } = useTipTapEditorContext();

  const hasAttributesForPreview = (linkPreview: LinkPreview) =>
    linkPreview.title || linkPreview.description;

  const setPreviewLinks = useEffectEvent((urls: any[]) => {
    // Create new linkPreviews object with only URLs that are in linkHrefs
    const updatedLinkPreviews = Object.keys(linkPreviews)
      .filter(url => linkHrefs.includes(url))
      .reduce(
        (acc, url) => {
          acc[url] = linkPreviews[url];
          return acc;
        },
        {} as Record<string, boolean>,
      );

    const newLinks = urls.filter(
      item => !Object.keys(linkPreviews).includes(item.url),
    );

    // Add new links
    const finalLinkPreviews = {
      ...updatedLinkPreviews,
      ...newLinks.reduce(
        (acc, item) => {
          acc[item.url] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    };

    setLinkPreviews(finalLinkPreviews);
  });

  const {
    data: unfurledLinks = [],
    isInitialLoading,
    isFetched,
  } = useQuery({
    queryKey: ["unfurlURLs", linkHrefs],

    queryFn: () =>
      reactQueryGet(
        internalApi.richTextFields.unfurlURLs({ params: { urls: linkHrefs } }),
      ),

    enabled: linkHrefs.length > 0,

    // This is to prevent empty screen when new items are fetched
    placeholderData: keepPreviousData,

    initialData: [],
  });

  const getPreviewableLinks = useEffectEvent((links: LinkPreview[]) =>
    links.filter(
      unfurledLink =>
        linkHrefs.includes(unfurledLink.url) &&
        hasAttributesForPreview(unfurledLink),
    ),
  );

  // Shows or hides the preview block based on fetched previews. Waits for a
  // real fetch because initialData is [] and would hide saved previews on load.
  useEffect(() => {
    if (!isFetched) return;
    const urls = getPreviewableLinks(unfurledLinks);
    setHasLinks(urls.length > 0);
  }, [isFetched, unfurledLinks]);

  // Syncs the dismissed/visible map with the links in the editor. Runs on
  // initialData too, so removing a link clears its dismissed flag and the
  // preview shows again when the link is re-added.
  useEffect(() => {
    if (editor.isEditable) {
      const urls = getPreviewableLinks(unfurledLinks);
      setPreviewLinks(urls);
    }
  }, [unfurledLinks, editor.isEditable, linkHrefs.length]);

  const filteredUnfurledLinks = unfurledLinks.filter(
    (unfurledLink: any) =>
      !Object.keys(linkPreviews).includes(unfurledLink.url) ||
      Object.keys(linkPreviews).some(
        (url: any) => url === unfurledLink.url && linkPreviews[url] === true,
      ),
  );

  return {
    unfurledLinks,
    isLoading: isInitialLoading,
    filteredUnfurledLinks,
  };
};
